import React from 'react';
import {
  Button,
  MultiSelect,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@roger/ui-components';
import { ChevronDown } from 'lucide-react';
import {
  MEMBER_STATUS_LABELS,
  MEMBER_STATUS_OPTIONS,
  MEMBER_STATUS_ORDER,
} from './memberStatus';
import type { MemberStatus } from './types';

type MemberStatusFilterProps = {
  value: MemberStatus[];
  onChange: (value: MemberStatus[]) => void;
};

/**
 * Which member states the table is showing. A checklist rather than a single
 * choice, because the useful views are combinations -- everybody who can still
 * be scheduled, or every outstanding invitation whether or not it has lapsed.
 */
export const MemberStatusFilter: React.FC<MemberStatusFilterProps> = ({
  value,
  onChange,
}) => {
  // Four options, so the trigger names them rather than counting them. Listed
  // in checklist order, not click order, so the label does not reshuffle as
  // boxes are ticked.
  const selected = MEMBER_STATUS_ORDER.filter(status => value.includes(status));
  const label =
    selected.length === MEMBER_STATUS_ORDER.length
      ? 'All statuses'
      : selected.length === 0
        ? 'No statuses'
        : selected.map(status => MEMBER_STATUS_LABELS[status]).join(', ');

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant='outline'
          // No height override: Button's default size is h-8, the same as the
          // search Input beside it, so the filter row sits on one baseline.
          className='w-full justify-between font-normal'
        >
          <span className='min-w-0 truncate' title={label}>
            {label}
          </span>
          <ChevronDown className='h-4 w-4 shrink-0 opacity-50' />
        </Button>
      </PopoverTrigger>
      <PopoverContent align='start' className='w-60'>
        <p className='mb-3 text-sm text-muted-foreground'>Filter by status</p>
        <MultiSelect
          options={MEMBER_STATUS_OPTIONS}
          value={value}
          onChange={next => onChange(next as MemberStatus[])}
          idPrefix='member-status-'
          optionRowClassName='gap-3'
          className='gap-3'
        />
      </PopoverContent>
    </Popover>
  );
};
